import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";
import he from "he";

import {
  Bell,
  CalendarDays,
  ArrowLeft,
  ArrowUpRight,
  FileText,
  Image as ImageIcon,
  Download,
  Clock,
  Search,
  ChevronRight,
  RefreshCw,
  Sparkles,
  Megaphone,
  X,
} from "lucide-react";

import LogoFallback from "/img/logo.png";

export default function NoticeDetails({
  logoSrc = LogoFallback,
  accent = "#014864",
  pageTitle = "Notice | University of Creative Technology Chittagong",
}) {
  const { id } = useParams();

  const [allnotice, setAllnotice] = useState([]);
  const [notice, setNotice] = useState(null);
  const [selectedNotice, setSelectedNotice] = useState(null);

  const [loading, setLoading] = useState(true);
  const [recentLoading, setRecentLoading] = useState(true);

  const [error, setError] = useState(null);
  const [recentError, setRecentError] = useState(null);

  const [searchRecent, setSearchRecent] = useState("");

  // --------------------------------------------------
  // API Configuration
  // --------------------------------------------------

  const api = useMemo(() => {
    return axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }, []);

  // --------------------------------------------------
  // Fetch Notice Details
  // --------------------------------------------------

  const handlePost = async () => {
    setLoading(true);
    setError(null);
    setSelectedNotice(null);
    setNotice(null);

    try {
      const payload = {
        userId: import.meta.env.VITE_API_USER,
        password: import.meta.env.VITE_API_PASS,
        id: id,
      };

      const response = await api.post("/notice-details", payload);

      const data = Array.isArray(response.data)
        ? response.data
        : response.data
          ? [response.data]
          : [];

      if (data.length === 0) {
        setError("Notice information was not found.");
        return;
      }

      setNotice(data);
      setSelectedNotice(data[0]);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Unable to load notice."
      );
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------
  // Fetch Recent Notices
  // --------------------------------------------------

  const noticePost = async () => {
    setRecentLoading(true);
    setRecentError(null);

    try {
      const payload = {
        userId: import.meta.env.VITE_API_USER,
        password: import.meta.env.VITE_API_PASS,
        department_id: "0",
      };

      const response = await api.post("/recent-notice", payload);

      setAllnotice(
        Array.isArray(response.data)
          ? response.data
          : []
      );
    } catch (err) {
      setRecentError(
        err.response?.data?.message ||
          err.message ||
          "Unable to load recent notices."
      );
    } finally {
      setRecentLoading(false);
    }
  };

  // --------------------------------------------------
  // Effects
  // --------------------------------------------------

  useEffect(() => {
    handlePost();
    noticePost();
  }, [id]);

  useEffect(() => {
    document.title = selectedNotice
      ? `${he.decode(selectedNotice.head_line || "Notice")} | UCTC`
      : pageTitle;
  }, [selectedNotice, pageTitle]);

  // --------------------------------------------------
  // Helpers
  // --------------------------------------------------

  const decodeTitle = (rawTitle) => {
    return he.decode(String(rawTitle || ""));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Date unavailable";

    const date = new Date(dateStr);

    if (Number.isNaN(date.getTime())) {
      return dateStr;
    }

    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getDay = (dateStr) => {
    if (!dateStr) return "--";

    const date = new Date(dateStr);

    if (Number.isNaN(date.getTime())) {
      return "--";
    }

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
    });
  };

  const getMonth = (dateStr) => {
    if (!dateStr) return "---";

    const date = new Date(dateStr);

    if (Number.isNaN(date.getTime())) {
      return "---";
    }

    return date
      .toLocaleDateString("en-GB", {
        month: "short",
      })
      .toUpperCase();
  };

  const getFileMimeType = (url) => {
    if (!url) return "unknown";

    const cleanUrl = url.split("?")[0].split("#")[0];
    const ext = cleanUrl.split(".").pop()?.toLowerCase();

    if (
      ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)
    ) {
      return "image";
    }

    if (ext === "pdf") {
      return "pdf";
    }

    return "unknown";
  };

  const getFileUrl = (path) => {
    if (!path) return "";

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const baseUrl = import.meta.env.VITE_API_SOURCE_URL || "";

    return `${baseUrl.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;
  };

  const textFormat = (textdata) => {
    if (!textdata) return "";

    return String(textdata)
      .replace(/\\r\\n/g, "\n")
      .replace(/\\n/g, "\n")
      .replace(/\r\n/g, "\n")
      .replace(/\n/g, "<br />");
  };

  // --------------------------------------------------
  // Selected Notice Attachment
  // --------------------------------------------------

  const attachmentUrl = selectedNotice
    ? getFileUrl(selectedNotice.images_path)
    : "";

  const attachmentType = selectedNotice
    ? getFileMimeType(selectedNotice.images_path)
    : "unknown";

  // --------------------------------------------------
  // Filter Recent Notices
  // --------------------------------------------------

  const filteredRecentNotices = allnotice
    .filter((item) => {
      const title = decodeTitle(item.head_line).toLowerCase();

      return title.includes(searchRecent.toLowerCase());
    })
    .slice(0, 15);

  // --------------------------------------------------
  // Animation Variants
  // --------------------------------------------------

  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 25,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const listVariants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.07,
      },
    },
  };

  const recentItemVariants = {
    hidden: {
      opacity: 0,
      x: 20,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.4,
      },
    },
  };

  // --------------------------------------------------
  // Render
  // --------------------------------------------------

  return (
    <div
      className="min-h-screen bg-slate-50 overflow-hidden"
      style={{
        "--accent": accent,
      }}
    >
      {/* ================================================
          HERO SECTION
      ================================================ */}

      <section className="relative bg-[#014864] text-white overflow-hidden">

        {/* Gradient Background */}

        <div className="absolute inset-0 bg-gradient-to-br from-[#014864] via-[#036987] to-[#0c9db0]" />

        {/* Animated Background */}

        <motion.div
          animate={{
            x: [0, 35, 0],
            y: [0, -25, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-cyan-300/10 blur-3xl"
        />

        <motion.div
          animate={{
            x: [0, -30, 0],
            y: [0, 25, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -bottom-40 -left-40 w-[450px] h-[450px] rounded-full bg-teal-300/10 blur-3xl"
        />

        {/* Decorative Grid */}

        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-16 md:py-20">

          {/* Breadcrumb */}

          <motion.div
            initial={{
              opacity: 0,
              x: -20,
            }}
            animate={{
              opacity: 1,
              x: 0,
            }}
            transition={{
              duration: 0.6,
            }}
            className="mb-10"
          >

            <Link
              to="/all-notice"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white transition text-sm font-medium"
            >
              <ArrowLeft size={17} />

              All Notices

              <ChevronRight size={15} />

              <span className="text-white">
                Notice Details
              </span>

            </Link>

          </motion.div>

          <div className="grid lg:grid-cols-[1fr_auto] gap-10 items-center">

            {/* Hero Content */}

            <motion.div
              initial={{
                opacity: 0,
                y: 35,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.8,
              }}
            >

              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-md mb-6">

                <Sparkles
                  size={16}
                  className="text-yellow-300"
                />

                <span className="text-sm font-medium tracking-wide">
                  Official University Announcement
                </span>

              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight max-w-4xl">

                {selectedNotice ? (
                  decodeTitle(selectedNotice.head_line)
                ) : loading ? (
                  "Loading Notice..."
                ) : (
                  "Notice Details"
                )}

              </h1>

              <p className="mt-6 text-white/75 text-base md:text-lg leading-relaxed max-w-2xl">

                Stay updated with the latest academic
                announcements, examination schedules,
                university events, and official notifications.

              </p>

              {selectedNotice && (
                <div className="flex flex-wrap items-center gap-4 mt-7">

                  <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-4 py-2">

                    <CalendarDays
                      size={16}
                      className="text-cyan-200"
                    />

                    <span className="text-sm text-white/90">
                      {formatDate(selectedNotice.date)}
                    </span>

                  </div>

                  <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-4 py-2">

                    <FileText
                      size={16}
                      className="text-teal-200"
                    />

                    <span className="text-sm text-white/90">
                      Official Notice
                    </span>

                  </div>

                </div>
              )}

            </motion.div>

            {/* Hero Illustration */}

            <motion.div
              initial={{
                opacity: 0,
                scale: 0.8,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              transition={{
                duration: 0.8,
                delay: 0.2,
              }}
              className="hidden md:flex justify-center items-center"
            >

              <motion.div
                animate={{
                  y: [0, -12, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative w-64 h-64 lg:w-72 lg:h-72"
              >

                <div className="absolute inset-0 rounded-full border border-white/20" />

                <div className="absolute inset-8 rounded-full border border-white/10" />

                <div className="absolute inset-16 rounded-full bg-white/5 backdrop-blur-sm" />

                <div className="absolute inset-0 flex items-center justify-center">

                  <div className="w-40 h-48 bg-white rounded-3xl shadow-2xl p-5 rotate-[-5deg]">

                    <div className="w-12 h-12 rounded-2xl bg-[#014864] flex items-center justify-center mb-5">

                      <Megaphone
                        size={24}
                        className="text-white"
                      />

                    </div>

                    <div className="space-y-3">

                      <div className="h-3 w-24 rounded-full bg-[#014864]" />

                      <div className="h-2 w-full rounded-full bg-slate-200" />

                      <div className="h-2 w-4/5 rounded-full bg-slate-200" />

                      <div className="h-2 w-full rounded-full bg-slate-200" />

                    </div>

                    <div className="mt-6 flex items-center gap-2">

                      <div className="w-8 h-8 rounded-lg bg-cyan-100" />

                      <div className="flex-1 space-y-2">

                        <div className="h-2 w-full rounded-full bg-slate-200" />

                        <div className="h-2 w-2/3 rounded-full bg-slate-200" />

                      </div>

                    </div>

                  </div>

                </div>

                <motion.div
                  animate={{
                    y: [0, -8, 0],
                    rotate: [0, 5, 0],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                  }}
                  className="absolute top-8 right-0 w-12 h-12 rounded-2xl bg-white shadow-xl flex items-center justify-center"
                >

                  <Bell
                    size={23}
                    className="text-[#014864]"
                  />

                </motion.div>

                <motion.div
                  animate={{
                    y: [0, 8, 0],
                    rotate: [0, -5, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                  }}
                  className="absolute bottom-8 left-0 w-12 h-12 rounded-2xl bg-cyan-300 shadow-xl flex items-center justify-center"
                >

                  <FileText
                    size={23}
                    className="text-[#014864]"
                  />

                </motion.div>

              </motion.div>

            </motion.div>

          </div>

        </div>

        {/* Bottom Wave */}

        <div className="relative -mb-1">

          <svg
            viewBox="0 0 1440 100"
            className="w-full h-16 md:h-20"
            preserveAspectRatio="none"
          >

            <path
              d="M0,50 C240,100 480,0 720,50 C960,100 1200,0 1440,50 L1440,100 L0,100 Z"
              fill="#f8fafc"
            />

          </svg>

        </div>

      </section>

      {/* ================================================
          MAIN CONTENT
      ================================================ */}

      <section className="relative py-12 md:py-16 px-4 sm:px-8 lg:px-12">

        <div className="max-w-7xl mx-auto">

          {/* Content Grid */}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">

            {/* ==========================================
                NOTICE ARTICLE
            ========================================== */}

            <div className="lg:col-span-2">

              {/* Loading State */}

              {loading && (

                <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 animate-pulse">

                  <div className="h-8 bg-slate-200 rounded-full w-3/4 mb-6" />

                  <div className="h-4 bg-slate-200 rounded-full w-1/3 mb-10" />

                  <div className="space-y-4">

                    <div className="h-4 bg-slate-200 rounded-full w-full" />

                    <div className="h-4 bg-slate-200 rounded-full w-5/6" />

                    <div className="h-4 bg-slate-200 rounded-full w-full" />

                    <div className="h-4 bg-slate-200 rounded-full w-2/3" />

                  </div>

                </div>

              )}

              {/* Error State */}

              {!loading && error && (

                <motion.div
                  initial={{
                    opacity: 0,
                    y: 20,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  className="bg-white rounded-3xl border border-red-100 shadow-sm p-10 text-center"
                >

                  <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-5">

                    <RefreshCw
                      size={28}
                      className="text-red-500"
                    />

                  </div>

                  <h2 className="text-2xl font-bold text-slate-800">
                    Notice Not Available
                  </h2>

                  <p className="mt-3 text-slate-500">
                    {error}
                  </p>

                  <button
                    onClick={handlePost}
                    className="mt-6 inline-flex items-center gap-2 bg-[#014864] text-white px-6 py-3 rounded-xl hover:bg-[#036987] transition"
                  >

                    <RefreshCw size={18} />

                    Try Again

                  </button>

                </motion.div>

              )}

              {/* Notice Article */}

              {!loading && !error && selectedNotice && (

                <motion.article
                  key={selectedNotice.id}
                  initial={{
                    opacity: 0,
                    y: 25,
                  }}
                  animate={{
                    opacity: 1,
                    y: 0,
                  }}
                  transition={{
                    duration: 0.6,
                  }}
                  className="bg-white rounded-3xl border border-slate-100 shadow-lg shadow-slate-200/50 overflow-hidden"
                >

                  {/* Article Header */}

                  <div className="relative px-6 md:px-10 pt-8 md:pt-10 pb-7 border-b border-slate-100">

                    <div className="flex items-center gap-2 text-[#014864] text-xs uppercase font-bold tracking-widest mb-5">

                      <span className="w-8 h-[2px] bg-[#014864]" />

                      Official Announcement

                    </div>

                    <h2 className="text-2xl md:text-3xl font-extrabold text-slate-800 leading-relaxed">

                      {decodeTitle(selectedNotice.head_line)}

                    </h2>

                    <div className="flex flex-wrap items-center gap-3 mt-6">

                      <div className="flex items-center gap-2 text-slate-500 text-sm">

                        <CalendarDays
                          size={17}
                          className="text-[#014864]"
                        />

                        {formatDate(selectedNotice.date)}

                      </div>

                      <span className="w-1 h-1 rounded-full bg-slate-300" />

                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full">

                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />

                        Published

                      </span>

                    </div>

                  </div>

                  {/* Article Body */}

                  <div className="px-6 md:px-10 py-8 md:py-10">

                    <div
                      className="notice-content prose prose-slate prose-lg max-w-none break-words
                      prose-headings:text-[#014864]
                      prose-headings:font-bold
                      prose-a:text-[#014864]
                      prose-a:font-semibold
                      prose-img:rounded-2xl
                      prose-img:shadow-lg
                      prose-table:border-collapse
                      prose-th:bg-slate-50
                      prose-th:p-3
                      prose-td:p-3"
                      dangerouslySetInnerHTML={{
                        __html: textFormat(
                          selectedNotice.description
                        ),
                      }}
                    />

                    {/* Image Attachment */}

                    {attachmentType === "image" && (

                      <motion.div
                        initial={{
                          opacity: 0,
                          scale: 0.97,
                        }}
                        animate={{
                          opacity: 1,
                          scale: 1,
                        }}
                        transition={{
                          duration: 0.5,
                        }}
                        className="mt-10"
                      >

                        <div className="flex items-center justify-between mb-4">

                          <div className="flex items-center gap-2 text-slate-700 font-bold">

                            <ImageIcon
                              size={20}
                              className="text-[#014864]"
                            />

                            Notice Attachment

                          </div>

                          <a
                            href={attachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm font-semibold text-[#014864] hover:text-cyan-600 transition"
                          >

                            <Download size={16} />

                            View Image

                          </a>

                        </div>

                        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-2">

                          <img
                            src={attachmentUrl}
                            alt={decodeTitle(
                              selectedNotice.head_line
                            )}
                            loading="lazy"
                            className="w-full h-auto max-h-[900px] object-contain rounded-xl"
                          />

                        </div>

                      </motion.div>

                    )}

                    {/* PDF Attachment */}

                    {attachmentType === "pdf" && (

                      <motion.div
                        initial={{
                          opacity: 0,
                          y: 20,
                        }}
                        animate={{
                          opacity: 1,
                          y: 0,
                        }}
                        transition={{
                          duration: 0.5,
                        }}
                        className="mt-10"
                      >

                        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">

                          <div className="flex items-center gap-2 text-slate-700 font-bold">

                            <FileText
                              size={20}
                              className="text-[#014864]"
                            />

                            Notice Document

                          </div>

                          <a
                            href={attachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-[#014864] text-white text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-[#036987] transition"
                          >

                            <Download size={16} />

                            Download PDF

                          </a>

                        </div>

                        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">

                          <iframe
                            src={`${attachmentUrl}#zoom=page-width&view=FitH&toolbar=0&navpanes=0`}
                            title="Notice PDF"
                            className="w-full h-[600px] md:h-[900px] border-0"
                          />

                        </div>

                      </motion.div>

                    )}

                    {/* Footer */}

                    <div className="mt-10 pt-6 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4">

                      <div className="flex items-center gap-2 text-slate-400 text-sm">

                        <Bell size={16} />

                        University of Creative Technology Chittagong

                      </div>

                      <Link
                        to="/all-notice"
                        className="inline-flex items-center gap-2 text-sm font-bold text-[#014864] hover:text-cyan-600 transition"
                      >

                        All Notices

                        <ArrowUpRight size={17} />

                      </Link>

                    </div>

                  </div>

                </motion.article>

              )}

            </div>

            {/* ==========================================
                RECENT NOTICES SIDEBAR
            ========================================== */}

            <aside className="lg:sticky lg:top-24">

              <motion.div
                initial={{
                  opacity: 0,
                  x: 35,
                }}
                whileInView={{
                  opacity: 1,
                  x: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  duration: 0.7,
                }}
                className="bg-white rounded-3xl border border-slate-100 shadow-lg shadow-slate-200/50 overflow-hidden"
              >

                {/* Sidebar Header */}

                <div className="relative bg-gradient-to-br from-[#014864] to-[#0785a3] px-6 py-7 text-white overflow-hidden">

                  <div className="absolute -right-10 -top-10 w-32 h-32 rounded-full bg-white/10" />

                  <div className="absolute -right-5 -bottom-16 w-36 h-36 rounded-full bg-white/5" />

                  <div className="relative z-10 flex items-center justify-between">

                    <div>

                      <div className="flex items-center gap-2 text-cyan-200 text-xs font-bold uppercase tracking-widest mb-3">

                        <Sparkles size={15} />

                        Stay Updated

                      </div>

                      <h2 className="text-2xl font-extrabold">
                        Recent Notices
                      </h2>

                      <p className="text-white/65 text-sm mt-2">
                        Latest university announcements
                      </p>

                    </div>

                    <div className="w-12 h-12 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center">

                      <Bell size={23} />

                    </div>

                  </div>

                </div>

                {/* Sidebar Search */}

                <div className="p-4 border-b border-slate-100">

                  <div className="relative">

                    <Search
                      size={17}
                      className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                    />

                    <input
                      type="text"
                      value={searchRecent}
                      onChange={(e) =>
                        setSearchRecent(e.target.value)
                      }
                      placeholder="Search recent notices..."
                      className="w-full pl-10 pr-10 py-3 rounded-xl bg-slate-50 border border-slate-200 text-sm outline-none focus:bg-white focus:border-[#014864] focus:ring-4 focus:ring-[#014864]/10 transition"
                    />

                    {searchRecent && (

                      <button
                        type="button"
                        onClick={() => setSearchRecent("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500"
                      >

                        <X size={16} />

                      </button>

                    )}

                  </div>

                </div>

                {/* Recent Notices List */}

                <div className="p-4">

                  {recentLoading && (

                    <div className="space-y-4">

                      {[1, 2, 3, 4].map((item) => (

                        <div
                          key={item}
                          className="animate-pulse flex gap-3"
                        >

                          <div className="w-12 h-12 bg-slate-200 rounded-xl shrink-0" />

                          <div className="flex-1 space-y-3 py-1">

                            <div className="h-3 bg-slate-200 rounded-full w-1/3" />

                            <div className="h-3 bg-slate-200 rounded-full w-full" />

                          </div>

                        </div>

                      ))}

                    </div>

                  )}

                  {!recentLoading && recentError && (

                    <div className="text-center py-8">

                      <p className="text-sm text-red-500">
                        {recentError}
                      </p>

                      <button
                        onClick={noticePost}
                        className="mt-4 text-sm font-bold text-[#014864]"
                      >

                        Try Again

                      </button>

                    </div>

                  )}

                  {!recentLoading &&
                    !recentError &&
                    filteredRecentNotices.length > 0 && (

                      <motion.div
                        variants={listVariants}
                        initial="hidden"
                        animate="visible"
                        className="space-y-2"
                      >

                        <AnimatePresence mode="popLayout">

                          {filteredRecentNotices.map((n, index) => (

                            <motion.button
                              key={n.id ?? index}
                              type="button"
                              variants={recentItemVariants}
                              layout
                              initial={{
                                opacity: 0,
                                x: 15,
                              }}
                              animate={{
                                opacity: 1,
                                x: 0,
                              }}
                              exit={{
                                opacity: 0,
                                x: -10,
                              }}
                              transition={{
                                duration: 0.3,
                              }}
                              onClick={() => {
                                setSelectedNotice(n);
                                setNotice([n]);

                                window.scrollTo({
                                  top: 0,
                                  behavior: "smooth",
                                });
                              }}
                              className={`group w-full text-left p-3 rounded-2xl border transition-all duration-300 ${
                                selectedNotice?.id === n.id
                                  ? "bg-cyan-50 border-cyan-200 shadow-sm"
                                  : "bg-white border-transparent hover:bg-slate-50 hover:border-slate-100"
                              }`}
                            >

                              <div className="flex items-start gap-3">

                                {/* Date Badge */}

                                <div className={`shrink-0 w-12 h-12 rounded-xl flex flex-col items-center justify-center transition ${
                                  selectedNotice?.id === n.id
                                    ? "bg-[#014864] text-white"
                                    : "bg-slate-100 text-[#014864] group-hover:bg-cyan-100"
                                }`}>

                                  <span className="text-lg font-extrabold leading-none">

                                    {getDay(n.date)}

                                  </span>

                                  <span className="text-[9px] font-bold tracking-wider mt-1">

                                    {getMonth(n.date)}

                                  </span>

                                </div>

                                {/* Notice Text */}

                                <div className="flex-1 min-w-0">

                                  <p className="text-[11px] text-slate-400 font-medium mb-1">

                                    {formatDate(n.date)}

                                  </p>

                                  <p className={`text-sm font-semibold leading-relaxed line-clamp-3 transition ${
                                    selectedNotice?.id === n.id
                                      ? "text-[#014864]"
                                      : "text-slate-700 group-hover:text-[#014864]"
                                  }`}>

                                    {decodeTitle(n.head_line)}

                                  </p>

                                </div>

                                <ChevronRight
                                  size={16}
                                  className={`shrink-0 mt-1 transition ${
                                    selectedNotice?.id === n.id
                                      ? "text-[#014864]"
                                      : "text-slate-300 group-hover:text-[#014864] group-hover:translate-x-1"
                                  }`}
                                />

                              </div>

                            </motion.button>

                          ))}

                        </AnimatePresence>

                      </motion.div>

                    )}

                  {!recentLoading &&
                    !recentError &&
                    filteredRecentNotices.length === 0 && (

                      <div className="text-center py-10">

                        <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">

                          <Search
                            size={23}
                            className="text-slate-400"
                          />

                        </div>

                        <p className="text-sm font-semibold text-slate-700">
                          No notices found
                        </p>

                        <p className="text-xs text-slate-400 mt-2">
                          Try another keyword.
                        </p>

                      </div>

                    )}

                </div>

                {/* Sidebar Footer */}

                <div className="px-5 pb-5">

                  <Link
                    to="/all-notice"
                    className="group flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl bg-[#014864] text-white font-bold text-sm hover:bg-[#036987] transition-all duration-300"
                  >

                    View All Notices

                    <ArrowUpRight
                      size={18}
                      className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                    />

                  </Link>

                </div>

              </motion.div>

              {/* Sidebar Bottom Card */}

              <motion.div
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                }}
                transition={{
                  duration: 0.6,
                }}
                className="mt-6 relative overflow-hidden rounded-3xl bg-gradient-to-br from-cyan-50 to-teal-50 border border-cyan-100 p-6"
              >

                <div className="absolute -right-8 -top-8 w-28 h-28 rounded-full bg-cyan-200/30" />

                <div className="relative z-10">

                  <div className="w-12 h-12 rounded-2xl bg-white shadow-sm flex items-center justify-center mb-4">

                    <Megaphone
                      size={24}
                      className="text-[#014864]"
                    />

                  </div>

                  <h3 className="text-lg font-extrabold text-[#014864]">
                    Stay Informed
                  </h3>

                  <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                    Check the official notice board regularly
                    for important university updates.
                  </p>

                  <Link
                    to="/all-notice"
                    className="inline-flex items-center gap-2 mt-4 text-sm font-bold text-[#014864] hover:text-cyan-700 transition"
                  >

                    Explore Notices

                    <ChevronRight
                      size={17}
                      className="transition-transform group-hover:translate-x-1"
                    />

                  </Link>

                </div>

              </motion.div>

            </aside>

          </div>

        </div>

      </section>

    </div>
  );
}

NoticeDetails.propTypes = {
  logoSrc: PropTypes.string,
  accent: PropTypes.string,
  pageTitle: PropTypes.string,
};
